import {
  isAgreementComplete,
  isClosingComplete,
  isPaymentComplete,
} from './constants/transaction';
import type {
  InvestmentDecision,
  InvestmentProject,
  Lead,
  LeadStatus,
  Transaction,
} from './types';

export type {
  AgreementStatus,
  ClosingStatus,
  PaymentStatus,
  TransactionStage,
} from './constants/transaction';
export {
  AGREEMENT_STATUSES,
  CLOSING_STATUSES,
  getTransactionStepIndex,
  isAgreementComplete,
  isClosingComplete,
  isPaymentComplete,
  PAYMENT_STATUSES,
  TRANSACTION_STAGES,
  TRANSACTION_STEP_LABELS,
} from './constants/transaction';

export const isLeadConverted = (lead: Lead) => lead.status === '已立项';

export const canConvertLead = (lead: Lead) =>
  lead.canConvert ?? lead.status === '初筛通过';

export const canSubmitLeadScreening = (status: LeadStatus) =>
  status === '初筛中';

export const canClaimLead = (status: LeadStatus) => status === '待初筛';

export const isLeadLocked = (lead: Lead) =>
  isLeadConverted(lead) ||
  lead.status === '已关闭' ||
  lead.status === '初筛不通过';

export const getLeadNextActionLabel = (lead: Lead) => {
  if (lead.status === '已立项') {
    return '已转入投资项目，请在投资项目继续推进';
  }
  if (lead.status === '已关闭' || lead.status === '初筛不通过') {
    return '线索已关闭，无后续行动';
  }
  if (lead.status === '待初筛') {
    return lead.nextAction?.trim() || '认领线索并开始初筛';
  }
  if (lead.status === '初筛中') {
    return (
      lead.nextAction?.trim() || '提交初筛意见（推荐立项 / 暂缓 / 不推荐）'
    );
  }
  if (lead.status === '初筛通过') {
    return lead.nextAction?.trim() || '转为正式投资项目';
  }
  if (lead.status === '暂缓') {
    return lead.nextAction?.trim() || '可恢复线索后继续初筛';
  }
  return lead.nextAction?.trim() || '暂无';
};

export type WorkflowPrimaryKey =
  | 'claim'
  | 'screen'
  | 'convert'
  | 'view-project'
  | 'advance-stage'
  | 'go-diligence'
  | 'submit-decision'
  | 'view-decision'
  | 'view-transaction'
  | 'update-result'
  | 'advance-progress'
  | 'complete-closing'
  | 'none';

export interface WorkflowStepHint {
  currentLabel: string;
  title: string;
  description?: string;
  primaryKey: WorkflowPrimaryKey;
  primaryLabel: string;
  done?: boolean;
}

export const resolveLeadWorkflow = (lead: Lead): WorkflowStepHint => {
  if (lead.status === '已立项') {
    return {
      currentLabel: '线索 · 已立项',
      title: '线索已转入投资项目',
      description: '后续请在项目详情推进尽调、投决与交割。',
      primaryKey: 'view-project',
      primaryLabel: '前往投资项目',
      done: true,
    };
  }
  if (lead.status === '已关闭' || lead.status === '初筛不通过') {
    return {
      currentLabel: `线索 · ${lead.status}`,
      title: '本线索已结束',
      description: '如需重启，可使用「恢复」或新建线索。',
      primaryKey: 'none',
      primaryLabel: '',
      done: true,
    };
  }
  if (canClaimLead(lead.status)) {
    return {
      currentLabel: '线索 · 待初筛',
      title: '认领并开始初筛',
      description: '认领后进入初筛中，即可提交初筛意见。',
      primaryKey: 'claim',
      primaryLabel: '认领并开始初筛',
    };
  }
  if (canSubmitLeadScreening(lead.status)) {
    return {
      currentLabel: '线索 · 初筛中',
      title: '提交初筛意见',
      description: '推荐立项后可转投资项目；也可暂缓或不推荐。',
      primaryKey: 'screen',
      primaryLabel: '提交初筛',
    };
  }
  if (canConvertLead(lead)) {
    return {
      currentLabel: '线索 · 初筛通过',
      title: '转为正式投资项目',
      description: '将创建投资项目并保留与本线索的关联。',
      primaryKey: 'convert',
      primaryLabel: '转立项',
    };
  }
  if (lead.status === '暂缓') {
    return {
      currentLabel: '线索 · 暂缓',
      title: '恢复后再继续推进',
      description: getLeadNextActionLabel(lead),
      primaryKey: 'none',
      primaryLabel: '',
    };
  }
  return {
    currentLabel: `线索 · ${lead.status}`,
    title: getLeadNextActionLabel(lead),
    primaryKey: 'none',
    primaryLabel: '',
  };
};

export const resolveProjectWorkflow = (input: {
  project: InvestmentProject;
  decisionSubmitted: boolean;
  canSubmitDecision: boolean;
  hasTransaction: boolean;
  blockerMessages?: string[];
}): WorkflowStepHint => {
  const { project, decisionSubmitted, canSubmitDecision, hasTransaction } =
    input;
  const stage = project.stage;

  if (stage === '暂缓' || stage === '否决') {
    return {
      currentLabel: `项目 · ${stage}`,
      title: '项目处于终态',
      description: '如需重启请使用「恢复」。',
      primaryKey: 'none',
      primaryLabel: '',
      done: true,
    };
  }
  if (stage === '已交割') {
    return {
      currentLabel: '项目 · 已交割',
      title: '交割已完成',
      description: '投后经营请前往投后管理；可回看交易详情。',
      primaryKey: hasTransaction ? 'view-transaction' : 'none',
      primaryLabel: hasTransaction ? '查看交易' : '',
      done: true,
    };
  }
  if (stage === '协议签订') {
    return {
      currentLabel: '项目 · 协议签订',
      title: '签署投资协议后再进入交易执行',
      description: '在交易详情归档已签署主协议。未签署不可推进阶段。',
      primaryKey: hasTransaction ? 'view-transaction' : 'none',
      primaryLabel: hasTransaction ? '前往交易签署协议' : '',
    };
  }
  if (stage === '交易执行') {
    return {
      currentLabel: '项目 · 交易执行',
      title: '推进打款与交割',
      description: '在交易详情更新付款与工商变更进度，条件齐备后完成交割。',
      primaryKey: 'view-transaction',
      primaryLabel: '前往交易与交割',
    };
  }
  if (stage === '投资决策' || decisionSubmitted) {
    return {
      currentLabel: '项目 · 投资决策',
      title: '录入或查看投决结果',
      description: '通过后进入协议签订并生成交易记录，签署完成再进入交易执行。',
      primaryKey: 'view-decision',
      primaryLabel: '前往投决详情',
    };
  }
  if (stage === '尽职调查') {
    if (canSubmitDecision) {
      return {
        currentLabel: '项目 · 尽职调查',
        title: '尽调就绪，提交投决',
        description: '提交后进入投决流程，由投决页录入会议结果。',
        primaryKey: 'submit-decision',
        primaryLabel: '提交投决',
      };
    }
    return {
      currentLabel: '项目 · 尽职调查',
      title: '完善尽调与风险后再提交投决',
      description:
        input.blockerMessages?.join('；') ||
        '请在「尽调与风险」页签完成必填项，或先更新项目阶段。',
      primaryKey: 'go-diligence',
      primaryLabel: '去完善尽调',
    };
  }
  if (stage === '立项' || stage === '项目评估') {
    return {
      currentLabel: `项目 · ${stage}`,
      title: stage === '项目评估' ? '确认评估结论并推进立项' : '进入尽职调查',
      description: '使用「更新阶段」沿流水线推进；阶段条仅作进度展示。',
      primaryKey: 'advance-stage',
      primaryLabel: '更新阶段',
    };
  }
  return {
    currentLabel: `项目 · ${stage}`,
    title: project.nextAction?.trim() || '继续推进项目',
    primaryKey: 'advance-stage',
    primaryLabel: '更新阶段',
  };
};

export const resolveDecisionWorkflow = (input: {
  decision: InvestmentDecision;
  hasTransaction: boolean;
}): WorkflowStepHint => {
  const { decision, hasTransaction } = input;
  if (hasTransaction && isDecisionFinalized(decision.status)) {
    return {
      currentLabel: `投决 · ${decision.status}`,
      title: '投决已通过，请推进交易交割',
      description: '交易记录已生成，在交易详情推进协议、付款与交割。',
      primaryKey: 'view-transaction',
      primaryLabel: '前往交易与交割',
      done: true,
    };
  }
  if (isDecisionFinalized(decision.status)) {
    return {
      currentLabel: `投决 · ${decision.status}`,
      title: '投决结果已确认',
      description:
        decision.status === '否决'
          ? '项目已否决，无需继续交易。'
          : '如尚未生成交易，请稍后刷新或返回项目查看。',
      primaryKey: 'none',
      primaryLabel: '',
      done: true,
    };
  }
  if (decision.status === '暂缓') {
    return {
      currentLabel: '投决 · 暂缓',
      title: '恢复后再更新投决结果',
      description: '暂缓期间不可录入最终结果。',
      primaryKey: 'none',
      primaryLabel: '',
    };
  }
  return {
    currentLabel: `投决 · ${decision.status}`,
    title: '录入投决会议结果',
    description: '选择通过 / 附条件通过 / 暂缓 / 否决。通过后将自动创建交易。',
    primaryKey: 'update-result',
    primaryLabel: '更新投决结果',
  };
};

export const resolveTransactionWorkflow = (input: {
  transaction: Transaction;
  canComplete: boolean;
  closed: boolean;
}): WorkflowStepHint => {
  const { transaction, canComplete, closed } = input;
  if (closed) {
    return {
      currentLabel: '交易 · 已交割',
      title: '交割已完成并已交接投后',
      description: '补充协议可在本页继续登记；经营跟踪请前往投后管理。',
      primaryKey: 'view-project',
      primaryLabel: '查看项目',
      done: true,
    };
  }
  if (canComplete) {
    return {
      currentLabel: `交易 · ${transaction.stage}`,
      title: '条件已齐备，完成交割',
      description: '确认最终投资金额后完成交割，系统将建档至投后。',
      primaryKey: 'complete-closing',
      primaryLabel: '完成交割',
    };
  }
  return {
    currentLabel: `交易 · ${transaction.stage}`,
    title: '推进协议、付款与交割进度',
    description:
      '先点「推进进度」更新三态；材料可在「交割清单 / 附件」补齐。完成交割需满足下方条件。',
    primaryKey: 'advance-progress',
    primaryLabel: '推进进度',
  };
};

const adjustStatusCount = (
  counts: Record<string, number>,
  status: string,
  delta: number,
) => {
  const next = { ...counts };
  const value = (next[status] ?? 0) + delta;
  if (value <= 0) delete next[status];
  else next[status] = value;
  return next;
};

export const shiftLeadStatusCounts = (
  counts: Record<string, number>,
  fromStatus?: string,
  toStatus?: string,
) => {
  let next = counts;
  if (fromStatus) next = adjustStatusCount(next, fromStatus, -1);
  if (toStatus) next = adjustStatusCount(next, toStatus, 1);
  return next;
};

export const findProjectByLeadId = (
  projects: { id: string; sourceLeadId?: string }[],
  leadId: string,
  relatedProjectId?: string,
) =>
  (relatedProjectId
    ? projects.find((item) => item.id === relatedProjectId)
    : undefined) ?? projects.find((item) => item.sourceLeadId === leadId);

export const FINAL_DECISION_STATUSES = new Set([
  '已通过',
  '附条件通过',
  '否决',
]);

export const isDecisionFinalized = (status: string) =>
  FINAL_DECISION_STATUSES.has(status);

export const isTransactionClosed = (transaction: { closingStatus: string }) =>
  transaction.closingStatus === '已完成';

export const buildTransactionChecklist = (transaction: Transaction) => [
  {
    key: 'agreement',
    title: '投资协议签署',
    owner: transaction.owner,
    status: transaction.agreementStatus,
    isComplete: isAgreementComplete(transaction.agreementStatus),
    materialAction: 'upload-agreement' as const,
  },
  {
    key: 'payment',
    title: '投资款支付',
    owner: transaction.owner,
    status: transaction.paymentStatus,
    isComplete: isPaymentComplete(transaction.paymentStatus),
    materialAction: 'upload-payment' as const,
  },
  {
    key: 'closing',
    title: '交割完成确认',
    owner: transaction.owner,
    status: transaction.closingStatus,
    isComplete: isClosingComplete(transaction.closingStatus),
    materialAction: 'upload-closing' as const,
  },
];

export interface ManagerStatRow {
  manager: string;
  projectCount: number;
  decisionCount: number;
  investedAmount: number;
  conversionRate: string;
}

export const aggregateManagerStats = (
  projects: InvestmentProject[],
  decisions: InvestmentDecision[],
  transactions: Transaction[],
): ManagerStatRow[] => {
  const managers = [
    ...new Set(projects.map((item) => item.manager).filter(Boolean)),
  ];
  return managers.map((manager) => {
    const managerProjects = projects.filter((item) => item.manager === manager);
    const managerDecisions = decisions.filter(
      (item) => item.manager === manager,
    );
    const investedAmount = transactions
      .filter(
        (item) => item.owner === manager && item.closingStatus === '已完成',
      )
      .reduce((sum, item) => sum + item.investmentAmount, 0);
    return {
      manager,
      projectCount: managerProjects.length,
      decisionCount: managerDecisions.length,
      investedAmount,
      conversionRate:
        managerProjects.length > 0
          ? `${((managerDecisions.length / managerProjects.length) * 100).toFixed(1)}%`
          : '0%',
    };
  });
};

const FUNNEL_COLORS = [
  '#1677ff',
  '#5a9cff',
  '#48bfd7',
  '#45c7a6',
  '#ffbd43',
  '#ff826f',
  '#f3a1ae',
];

export const buildFunnelSegments = (
  funnel: { label: string; value: number }[],
) =>
  funnel.map((item, index) => ({
    ...item,
    color: FUNNEL_COLORS[index % FUNNEL_COLORS.length],
  }));

export const buildSourceSegments = (
  distribution: { label: string; value: number }[],
) => {
  const palette = [
    '#347ff0',
    '#43bfd6',
    '#52ceb0',
    '#ffbd43',
    '#8a72e9',
    '#a9b9ed',
    '#bde6cc',
  ];
  return distribution.map((item, index) => ({
    ...item,
    color: palette[index % palette.length],
  }));
};

export const findDecisionByProjectId = (
  decisions: InvestmentDecision[],
  projectId: string,
) => decisions.find((item) => item.projectId === projectId);

export const findTransactionByProjectId = (
  transactions: Transaction[],
  projectId: string,
) => transactions.find((item) => item.projectId === projectId);
